import React, { useState, useRef } from 'react';
import { useClassName, useWindowSize } from 'common/hooks';
import { SVGIcon as Icon, optimizeContentfulImageUrl } from 'common/components';
import { CTA_TYPES, STANDARD_EVENTS } from 'utils/analytics/trackV2';
import useCustomStyle from '../../hooks';
import { Pagination, Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import './card-u.less';
import Link from 'public/screens/CustomLandingPage/Components/Link';
import Actions from 'public/screens/CustomLandingPage/Components/Actions';
import InfoModal from 'public/screens/CustomLandingPage/Components/InfoModal';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { usePagination } from 'public/screens/Home/Components/usePagination';
import { getStylesByPrefix } from '../../utils';
import { PriceOptionsSelector } from 'public/components/AttractionScreen/PriceOptionsSelector';
import { BottomSheet } from 'public/components/BottomSheet';

// Same convention as the park screens: an "oldPrice" keyValue in the
// entry settings, stored either as "180" or "$180".
export function getOldPrice(detail) {
  const setting = (detail?.fields?.settings || []).find(
    (item) => item?.fields?.key === 'oldPrice'
  );
  const value = setting?.fields?.value?.trim();
  if (!value) return null;
  return /^\d/.test(value) ? `$${value}` : value;
}

const parsePrice = (price) => parseFloat(String(price).replace(/[^0-9.]/g, ''));

// Lowest-priced option, shown as "From $X" in the mobile/tablet sticky bar.
export function getCheapestOption(options) {
  return options.reduce(
    (cheapest, option) =>
      !cheapest || parsePrice(option.price) < parsePrice(cheapest.price)
        ? option
        : cheapest,
    null
  );
}

export function CardInfoModal({ cta, infoModal, ctaStyles, handleTrack }) {
  const linkClassNames = useClassName('CLPLink');
  const [, ctaStyle] = useCustomStyle(cta?.fields?.settings);
  const modalTriggerStyles = {
    border: 0,
    fontFamily: 'inherit',
    ...getStylesByPrefix('--link-', ctaStyle),
    ...ctaStyles,
  };

  const handleOpen = () => {
    if (!handleTrack || !cta) return;

    handleTrack({
      event: STANDARD_EVENTS.click,
      params: {
        label: cta.fields.label,
        cta_type: CTA_TYPES.modal,
        destination_url: cta.fields.url,
      },
    });
  };

  return (
    <InfoModal
      component={infoModal}
      onOpen={handleOpen}
      trigger={
        cta ? (
          <button
            type="button"
            style={modalTriggerStyles}
            className={linkClassNames([
              cta.fields.theme !== 'other' && 'container',
              cta.fields.theme,
            ])}>
            {cta.fields.label}
          </button>
        ) : null
      }
    />
  );
}

export default function CardTypeU({
  item,
  refs,
  handleTrack,
  isActive,
  onSelect,
}) {
  const classNames = useClassName('CLPCardTypeU');
  const [setRef, style] = useCustomStyle(item.fields.settings);
  const ctas = item.fields.links || [];
  const title = item.fields.title;
  const subtitle = item.fields.subtitle;
  const description = item.fields.description;

  const itemDetails = item.fields.itemDetails;
  const carousel = item.fields.carousel;
  const links = item.fields.links || [];
  const infoModal = item.fields.infoModal;
  const pagination = usePagination({});
  const { isMobile, isTablet, isDesktop } = useWindowSize();
  const [hasMultipleSlides, setHasMultipleSlides] = useState(false);
  const [isPassSheetOpen, setIsPassSheetOpen] = useState(false);
  const containerRef = useRef(null);

  const isAccordionMode = Boolean(onSelect);
  const stateClass = isAccordionMode ? (isActive ? 'open' : 'closed') : null;
  const ctaStyles = getStylesByPrefix('--info-cta-', style);
  const isBundlesPass = style['--variant'] === 'bundles-pass';
  const isBundlesPassCompact =
    style['--bundles-pass-theme'] === 'compact-light';

  const handleSwiperInit = (swiper) => {
    const bullets = swiper.pagination.bullets;
    setHasMultipleSlides(bullets.length > 1);
  };

  const handleClick = () => {
    if (isActive) return;
    onSelect?.();

    if ((isMobile || isTablet) && containerRef.current) {
      setTimeout(() => {
        const element = containerRef.current;
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }, 100);
    }
  };

  const renderCarousel = () =>
    carousel && (
      <div className={classNames('swiper')}>
        <Swiper
          {...pagination}
          autoCorrect="on"
          modules={[Pagination, Autoplay]}
          centeredSlides={isMobile || isTablet ? true : false}
          spaceBetween={0}
          slidesPerView={1}
          speed={500}
          onSwiper={handleSwiperInit}
          preventClicksPropagation={true}
          className={classNames('carousel')}
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          loop={hasMultipleSlides}>
          {(carousel?.fields?.slides || []).map((slide, index) => {
            return (
              <SwiperSlide
                key={index}
                className={classNames('carousel__slide')}>
                {isMobile && (
                  <img
                    src={optimizeContentfulImageUrl(
                      slide.fields.mobile.fields.file.url,
                      slide.fields.mobile.fields.file.contentType
                    )}
                    alt={slide.fields.mobile.fields.description}
                  />
                )}
                {isTablet && (
                  <img
                    src={optimizeContentfulImageUrl(
                      slide.fields.tablet.fields.file.url,
                      slide.fields.tablet.fields.file.contentType
                    )}
                    alt={slide.fields.tablet.fields.description}
                  />
                )}
                {isDesktop && (
                  <img
                    src={optimizeContentfulImageUrl(
                      slide.fields.desktop.fields.file.url,
                      slide.fields.desktop.fields.file.contentType
                    )}
                    alt={slide.fields.desktop.fields.description}
                  />
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    );

  if (isBundlesPass) {
    const details = itemDetails?.fields?.items || [];
    const getDetailRole = (detail) => {
      const tags = detail.fields.tags || [];
      return tags.find((tag) =>
        ['feature', 'promotion', 'price'].includes(tag)
      );
    };
    const featureDetails = details.filter(
      (detail) => (getDetailRole(detail) || 'feature') === 'feature'
    );
    const promotion = details.find(
      (detail) => getDetailRole(detail) === 'promotion'
    );
    const priceDetails = details.filter(
      (detail) => getDetailRole(detail) === 'price'
    );
    const hasPriceOptions = priceDetails.length > 1;
    const pricing = !hasPriceOptions ? priceDetails[0] : null;
    const pricingOldPrice = getOldPrice(pricing);
    const priceOptions = hasPriceOptions
      ? priceDetails.map((detail, index) => ({
          name: detail.fields.subtitle,
          price: detail.fields.title,
          oldPrice: getOldPrice(detail),
          priceLabel: detail.fields.labels?.[0],
          description: detail.fields.labels?.[1],
          type: (detail.fields.tags || []).includes('best-value')
            ? 'primary'
            : undefined,
          link: links[index]?.fields?.url,
        }))
      : [];
    // On mobile/tablet the compact card is a sticky bar; the options open in
    // a bottom sheet instead of rendering inline.
    const showPassSheet =
      hasPriceOptions && isBundlesPassCompact && (isMobile || isTablet);
    const cheapestOption = showPassSheet
      ? getCheapestOption(priceOptions)
      : null;

    const renderDetailIcon = (detail) =>
      detail?.fields.icon?.fields?.file && (
        <img
          src={optimizeContentfulImageUrl(
            detail.fields.icon.fields.file.url,
            detail.fields.icon.fields.file.contentType
          )}
          alt=""
          aria-hidden="true"
        />
      );

    return (
      <div
        ref={(node) => {
          containerRef.current = node;
          setRef(node);
        }}
        className={classNames([
          'container',
          isBundlesPassCompact
            ? 'container--bundles-pass-compact-light'
            : 'container--bundles-pass',
        ])}>
        <div className={classNames('bundles-heading')}>
          {subtitle && (
            <div className={classNames('info-subtitle')}>{subtitle}</div>
          )}
          <div className={classNames('info-title')}>{title}</div>
        </div>
        {renderCarousel()}
        <div className={classNames('bundles-body')}>
          {description && (
            <div className={classNames('info-description')}>
              {documentToReactComponents(description)}
            </div>
          )}
          {featureDetails.length > 0 && (
            <div className={classNames('info-item-details')}>
              {featureDetails.map((detail) => (
                <div
                  key={detail.sys.id}
                  className={classNames('info-item-details-row')}>
                  <div className={classNames('info-item-details-row-icon')}>
                    {renderDetailIcon(detail)}
                  </div>
                  <div className={classNames('info-item-details-row-title')}>
                    {detail.fields.title}
                  </div>
                </div>
              ))}
            </div>
          )}
          {hasPriceOptions && showPassSheet && (
            <>
              <div className={classNames('bundles-pricing')}>
                <span className={classNames('bundles-pricing-label')}>
                  From
                </span>
                <div className={classNames('bundles-pricing-values')}>
                  <strong>{cheapestOption.price}</strong>
                </div>
              </div>
              <div className={classNames('info-cta')}>
                <button
                  type="button"
                  className={classNames('bundles-choose-cta')}
                  onClick={() => setIsPassSheetOpen(true)}>
                  Choose Your Pass
                </button>
              </div>
              <BottomSheet
                className="bundles-pass-sheet"
                isOpen={isPassSheetOpen}
                onClose={() => setIsPassSheetOpen(false)}
                title="Choose your pass"
                content={
                  <PriceOptionsSelector
                    layout="list"
                    options={priceOptions}
                    banner={promotion?.fields?.title}
                    bannerIcon={promotion?.fields?.icon}
                  />
                }
              />
            </>
          )}
          {hasPriceOptions && !showPassSheet && (
            <PriceOptionsSelector
              options={priceOptions}
              banner={promotion?.fields?.title}
              bannerIcon={promotion?.fields?.icon}
            />
          )}
          {!hasPriceOptions && (
            <>
              {promotion && (
                <div className={classNames('bundles-promotion')}>
                  {renderDetailIcon(promotion)}
                  <span>{promotion.fields.title}</span>
                </div>
              )}
              {pricing && (
                <div className={classNames('bundles-pricing')}>
                  {pricing.fields.subtitle && (
                    <span className={classNames('bundles-pricing-label')}>
                      {pricing.fields.subtitle}
                    </span>
                  )}
                  <div className={classNames('bundles-pricing-values')}>
                    <strong>{pricing.fields.title}</strong>
                    {pricingOldPrice && (
                      <s className={classNames('bundles-pricing-old')}>
                        {pricingOldPrice}
                      </s>
                    )}
                    {pricing.fields.labels?.[0] && (
                      <span>{pricing.fields.labels[0]}</span>
                    )}
                  </div>
                </div>
              )}
              {links.length > 0 && (
                <div className={classNames('info-cta')}>
                  {links.map((link) => (
                    <Link
                      key={link.sys.id}
                      component={link}
                      refs={refs}
                      handleTrack={handleTrack}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={(node) => {
        containerRef.current = node;
        setRef(node);
      }}
      className={classNames(['container', `container--${stateClass}`])}
      onClick={handleClick}
      style={{}}>
      {
        <>
          {renderCarousel()}
          <div className={classNames('info')}>
            <div className={classNames('info-subtitle')}>{subtitle}</div>
            <div className={classNames('info-title')}>{title}</div>
            {description && (
              <div className={classNames('info-description')}>
                {documentToReactComponents(description)}
              </div>
            )}
            {itemDetails && (
              <div className={classNames('info-item-details')}>
                {itemDetails?.fields?.items.map((detail) => (
                  <div
                    key={detail.sys.id}
                    className={classNames('info-item-details-row')}>
                    {detail.fields.icon?.fields?.file && (
                      <div className={classNames('info-item-details-row-icon')}>
                        <img
                          src={optimizeContentfulImageUrl(
                            detail.fields.icon.fields.file.url,
                            detail.fields.icon.fields.file.contentType
                          )}
                          alt={detail.fields.icon.fields.title}
                        />
                      </div>
                    )}
                    <div className={classNames('info-item-details-row-title')}>
                      {detail.fields.title}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(ctas.length > 0 || infoModal) && (
              <div className={classNames('info-cta')}>
                <Actions>
                  {infoModal ? (
                    <CardInfoModal
                      cta={ctas[0]}
                      ctaStyles={ctaStyles}
                      handleTrack={handleTrack}
                      infoModal={infoModal}
                    />
                  ) : (
                    ctas[0] && (
                      <Link
                        key={ctas[0].sys.id}
                        style={ctaStyles}
                        component={ctas[0]}
                        refs={refs}
                        handleTrack={handleTrack}
                      />
                    )
                  )}
                  {ctas.slice(1).map((cta) => (
                    <Link
                      key={cta.sys.id}
                      style={ctaStyles}
                      component={cta}
                      refs={refs}
                      handleTrack={handleTrack}
                    />
                  ))}
                </Actions>
              </div>
            )}
          </div>
          {isAccordionMode && (
            <div className={classNames('plus-button')}>
              <Icon name="add-minimal" size="small" />
            </div>
          )}
        </>
      }
    </div>
  );
}
